import React, { FC, PropsWithChildren, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Icon, Button } from '../../../shared/ui';
import { IconMap } from '../../../shared/sprite';
import { CreateSnippetButton } from '../../../features/snippets/ui/components';

import { useSnippetsPermissions } from '../../../features/teams';
import { snippetsModel } from '../../../features/snippets';

import { SNIPPET_NO_FOUND_MESSAGES } from '../lib';
import { useSnippetsActions } from '../hooks';

type Props = {
  type: 'team' | 'personal' | 'drafts';
};

export const NoSnippetsFound: React.FC<Props> = ({ type }) => {
  const dispatch = useDispatch();

  const { isCanManageOrCreatePersonalSnippets } = useSnippetsActions();
  const { isCanManageCategories, isCanCreatePersonalSnippets } =
    useSnippetsPermissions();

  const renderButton = useMemo(() => {
    if (type === 'team' && isCanManageCategories) {
      return (
        <NoSnippetsCard
          {...SNIPPET_NO_FOUND_MESSAGES.teamSnippetsCategoryManager}
        >
          <Button
            color="primary"
            size="lg"
            variant="text"
            className="!font-semibold"
            onClick={() =>
              dispatch(snippetsModel.actions.setIsCreateCategoryModalOpen(true))
            }
          >
            Create categories
          </Button>
        </NoSnippetsCard>
      );
    }

    if (type === 'team' && isCanManageOrCreatePersonalSnippets) {
      return (
        <NoSnippetsCard {...SNIPPET_NO_FOUND_MESSAGES.teamSnippetsManager}>
          <CreateSnippetButton size="lg" />
        </NoSnippetsCard>
      );
    }

    if (type === 'team' && !isCanManageOrCreatePersonalSnippets) {
      return (
        <NoSnippetsCard {...SNIPPET_NO_FOUND_MESSAGES.teamSnippetsMember} />
      );
    }

    if (type === 'personal' && isCanCreatePersonalSnippets) {
      return (
        <NoSnippetsCard {...SNIPPET_NO_FOUND_MESSAGES.personal}>
          <CreateSnippetButton size="lg" />
        </NoSnippetsCard>
      );
    }

    if (type === 'drafts') {
      return (
        <NoSnippetsCard {...SNIPPET_NO_FOUND_MESSAGES.drafts}>
          <CreateSnippetButton size="lg" />
        </NoSnippetsCard>
      );
    }

    return null;
  }, [
    dispatch,
    type,
    isCanCreatePersonalSnippets,
    isCanManageCategories,
    isCanManageOrCreatePersonalSnippets,
  ]);

  return (
    <div className="flex flex-col items-center justify-center flex-grow w-full gap-2 overflow-hidden">
      <div
        className="flex flex-col items-center justify-center w-120 h-120"
        style={{ backgroundImage: 'url(../../../assets/images/bg-dots.svg)' }}
      >
        <div className="border border-gray-200 rounded-2.5 p-3 shadow-xs w-12 h-12 bg-base-white relative">
          <Icon glyph={IconMap.Snippets} width={24} className="text-gray-700" />
        </div>
        {renderButton}
      </div>
    </div>
  );
};

type NoSnippetsCardProps = {
  title: string;
  description: string;
};

const NoSnippetsCard: FC<PropsWithChildren<NoSnippetsCardProps>> = ({
  title,
  description,
  children,
}) => {
  return (
    <div className="flex flex-col items-center justify-center mt-4">
      <p className="mb-1 font-semibold text-gray-900 text-md">{title}</p>
      <p className="text-gray-600 text-sm font-normal text-center mb-6 max-w-74">
        {description}
      </p>
      {children}
    </div>
  );
};
